import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { agendaLaboratorioHorarioService, AgendaLaboratorioHorario } from '../services/api';

const AgendaLaboratorioPage: React.FC = () => {
  const { agendaId } = useParams<{ laboratorioId: string; agendaId: string }>();
  const navigate = useNavigate();
  
  const [horariosAgenda, setHorariosAgenda] = useState<AgendaLaboratorioHorario[]>([]);
  const [loadingHorarios, setLoadingHorarios] = useState(false);
  const [error, setError] = useState('');
  const [editingMotivo, setEditingMotivo] = useState<string | null>(null);
  const [tempMotivo, setTempMotivo] = useState('');

  // const diasSemana = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

  const loadHorariosAgenda = useCallback(async () => {
    try {
      setLoadingHorarios(true);
      const data = await agendaLaboratorioHorarioService.getByAgenda(agendaId!);
      setHorariosAgenda(data);
    } catch (error) {
      console.error('Erro ao carregar horários da agenda:', error);
      setError('Erro ao carregar horários da agenda');
    } finally {
      setLoadingHorarios(false);
    }
  }, [agendaId]);

  useEffect(() => {
    if (agendaId) {
      loadHorariosAgenda();
    }
  }, [agendaId, loadHorariosAgenda]);

  const handleToggleDisponibilidade = async (horarioId: string, disponivel: boolean) => {
    try {
      const motivo = disponivel ? '' : 'Indisponível';
      const updatedHorario = await agendaLaboratorioHorarioService.updateDisponibilidade(horarioId, { 
        disponivel,
        indisponibilidadeMotivo: motivo
      });
      
      setHorariosAgenda(prev => 
        prev.map(h => h.id === horarioId ? { ...h, disponivel: updatedHorario.disponivel, indisponibilidadeMotivo: updatedHorario.indisponibilidadeMotivo } : h)
      );
    } catch (error) {
      console.error('Erro ao atualizar disponibilidade:', error);
      setError('Erro ao atualizar disponibilidade do horário');
    }
  };

  const handleToggleDayAvailability = async (diaSemana: number) => {
    const horariosDay = horariosAgenda.filter(h => h.horario.diaSemana === diaSemana);
    if (horariosDay.length === 0) return;

    // Determinar se deve tornar todos disponíveis ou indisponíveis
    const todosDisponiveis = horariosDay.every(h => h.disponivel);
    const novoStatus = !todosDisponiveis;
    const motivo = novoStatus ? '' : 'Indisponível';

    try {
      // Atualizar todos os horários do dia
      const promises = horariosDay.map(horario => 
        horario.id ? agendaLaboratorioHorarioService.updateDisponibilidade(horario.id, { 
          disponivel: novoStatus,
          indisponibilidadeMotivo: motivo
        }) : null
      ).filter(Boolean);

      await Promise.all(promises);

      // Atualizar o estado local
      setHorariosAgenda(prev => 
        prev.map(h => 
          h.horario.diaSemana === diaSemana ? { ...h, disponivel: novoStatus, indisponibilidadeMotivo: motivo } : h
        )
      );
    } catch (error) {
      console.error('Erro ao atualizar disponibilidade do dia:', error);
      setError('Erro ao atualizar disponibilidade dos horários do dia');
    }
  };

  const formatarHorario = (inicio: string, fim: string) => {
    return `${inicio} - ${fim}`;
  };

  const handleEditMotivo = (horarioId: string, currentMotivo: string) => {
    setEditingMotivo(horarioId);
    setTempMotivo(currentMotivo || 'Indisponível');
  };

  const handleSaveMotivo = async (horarioId: string) => {
    try {
      const motivo = tempMotivo.slice(0, 13); // Limitar a 13 caracteres
      const updatedHorario = await agendaLaboratorioHorarioService.updateDisponibilidade(horarioId, { 
        disponivel: false,
        indisponibilidadeMotivo: motivo
      });
      
      setHorariosAgenda(prev => 
        prev.map(h => h.id === horarioId ? { ...h, indisponibilidadeMotivo: updatedHorario.indisponibilidadeMotivo } : h)
      );
      
      setEditingMotivo(null);
      setTempMotivo('');
    } catch (error) {
      console.error('Erro ao atualizar motivo:', error);
      setError('Erro ao atualizar motivo de indisponibilidade');
    }
  };

  const handleCancelEdit = () => {
    setEditingMotivo(null);
    setTempMotivo('');
  };

  const handleBackToLaboratorios = () => {
    navigate('/laboratorios');
  };

  return (
    <div className="container-fluid py-4" style={{ marginTop: '62px' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-0">
            <i className="fas fa-flask me-2"></i>
            Laboratórios
          </h4>
          <small className="text-muted">Gerenciar Agenda do Laboratório</small>
        </div>
        <div>
          <button className="btn btn-outline-secondary" onClick={handleBackToLaboratorios}>
            <i className="fas fa-arrow-left me-2"></i>
            Voltar para Laboratórios
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError('')}
          ></button>
        </div>
      )}

      {/* Visualização de Horários da Agenda */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-clock me-2"></i>
                Gerenciar Horários da Agenda
              </h5>
            </div>
            <div className="card-body">
              
              <div className="alert alert-info mb-4">
                <div className="row">
                  <div className="col-md-6">
                    <strong>
                      <i className="fas fa-flask me-2"></i>
                      Laboratório:
                    </strong>
                    <span className="ms-2">
                      Agenda de Laboratório
                    </span>
                  </div>
                  <div className="col-md-6">
                    <strong>
                      <i className="fas fa-calendar me-2"></i>
                      Período:
                    </strong>
                    <span className="ms-2">
                      Gerenciamento de Horários
                    </span>
                  </div>
                </div>
              </div>

              {loadingHorarios ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando horários...</span>
                  </div>
                </div>
              ) : (
                <div>
                  {horariosAgenda.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="fas fa-clock fa-3x text-muted mb-3"></i>
                      <h5 className="text-muted">Nenhum horário encontrado</h5>
                      <p className="text-muted">Esta agenda não possui horários cadastrados.</p>
                    </div>
                  ) : (
                    <div className="row">
                      {/* Colunas dos dias da semana */}
                      {[1, 2, 3, 4, 5, 6, 0].map(diaSemana => {
                        const diasNomes = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
                        const horariosDay = horariosAgenda.filter(h => h.horario.diaSemana === diaSemana);
                        
                        if (horariosDay.length === 0) return null;
                        
                        return (
                          <div key={diaSemana} className="col-lg-2 col-md-4 col-sm-6 mb-4">
                            <div className="card" style={{ minHeight: '400px' }}>
                              <div 
                                className="card-header bg-primary text-white text-center" 
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleToggleDayAvailability(diaSemana)}
                                title="Clique para inverter a disponibilidade de todos os horários deste dia"
                              >
                                <h6 className="mb-0">{diasNomes[diaSemana]}</h6>
                                <small style={{ fontSize: '0.7em', opacity: 0.8 }}>Clique para inverter</small>
                              </div>
                              <div className="card-body p-2">
                                {horariosDay
                                  .sort((a, b) => (a.horario?.inicio || '').localeCompare(b.horario?.inicio || ''))
                                  .map((agendaHorario) => (
                                    <div 
                                      key={agendaHorario.id} 
                                      className={`card mb-2 ${agendaHorario.disponivel ? 'border-success' : 'border-danger'}`}
                                      style={{ cursor: 'pointer' }}
                                      onClick={(e) => {
                                        // Não executar toggle se estiver clicando no campo de edição
                                        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLButtonElement) {
                                          e.stopPropagation();
                                          return;
                                        }
                                        agendaHorario.id && handleToggleDisponibilidade(agendaHorario.id, !agendaHorario.disponivel);
                                      }}
                                    >
                                      <div className="card-body p-2 text-center">
                                        <div className={`mb-1 ${agendaHorario.disponivel ? 'text-success' : 'text-danger'}`}>
                                          <i className={`fas ${agendaHorario.disponivel ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                        </div>
                                        <small className="d-block">
                                          <strong>{formatarHorario(agendaHorario.horario?.inicio || '--:--', agendaHorario.horario?.fim || '--:--')}</strong>
                                        </small>
                                        
                                        {agendaHorario.disponivel ? (
                                          <span className="badge bg-success mt-1" style={{ fontSize: '0.7em' }}>
                                            Disponível
                                          </span>
                                        ) : (
                                          <div className="mt-1">
                                            {editingMotivo === agendaHorario.id ? (
                                              <div className="d-flex align-items-center justify-content-center" style={{ gap: '4px' }}>
                                                <input
                                                  type="text"
                                                  value={tempMotivo}
                                                  onChange={(e) => setTempMotivo(e.target.value.slice(0, 13))}
                                                  className="form-control form-control-sm"
                                                  style={{ 
                                                    fontSize: '0.7em', 
                                                    width: '80px',
                                                    height: '20px',
                                                    padding: '2px 4px'
                                                  }}
                                                  maxLength={13}
                                                  onClick={(e) => e.stopPropagation()}
                                                  autoFocus
                                                />
                                                <button
                                                  className="btn btn-sm btn-success"
                                                  style={{ 
                                                    fontSize: '0.6em', 
                                                    padding: '1px 4px',
                                                    height: '20px'
                                                  }}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    agendaHorario.id && handleSaveMotivo(agendaHorario.id);
                                                  }}
                                                >
                                                  <i className="fas fa-check"></i>
                                                </button>
                                                <button
                                                  className="btn btn-sm btn-secondary"
                                                  style={{ 
                                                    fontSize: '0.6em', 
                                                    padding: '1px 4px',
                                                    height: '20px'
                                                  }}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCancelEdit();
                                                  }}
                                                >
                                                  <i className="fas fa-times"></i>
                                                </button>
                                              </div>
                                            ) : (
                                              <div className="d-flex align-items-center justify-content-center" style={{ gap: '4px' }}>
                                                <span className="badge bg-danger" style={{ fontSize: '0.7em' }}>
                                                  {agendaHorario.indisponibilidadeMotivo || 'Indisponível'}
                                                </span>
                                                <button
                                                  className="btn btn-sm btn-outline-secondary"
                                                  style={{ 
                                                    fontSize: '0.6em', 
                                                    padding: '1px 4px',
                                                    height: '18px',
                                                    border: 'none'
                                                  }}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    agendaHorario.id && handleEditMotivo(agendaHorario.id, agendaHorario.indisponibilidadeMotivo || 'Indisponível');
                                                  }}
                                                  title="Editar motivo"
                                                >
                                                  <i className="fas fa-pencil-alt"></i>
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgendaLaboratorioPage;